import {
    AlertTriangle,
    ChartCandlestick,
    ChartLine,
    Info,
    Layers,
    Ruler,
} from 'lucide-react';
import React, { useRef, useState } from 'react';

import { Panel } from '@/components/ui/panel';
import {
    findMarket,
    useTokenPairStore,
    useTokenPairs,
} from '@/features/tokenPairs';
import { useMediaQuery } from '@/lib/hooks';
import { cn } from '@/lib/utils';

import type { PriceConfig } from '../utils/priceFormat';

import {
    formatDayChange,
    formatFunding,
    formatDislocation,
    formatOpenInterest,
    isDislocated,
} from './MarketRow';
import MobileMarketSheet from './MobileMarketSheet';
import TokenPairSelect from './TokenPairSelect';

type ChartMode = 'live' | 'candles';

interface SpeedChartHeaderProps {
    currentValue: number;
    priceFlash: 'up' | 'down' | null;
    priceConfig: PriceConfig;
    chartMode: ChartMode;
    onChartModeChange: (mode: ChartMode) => void;
    /** Liquidation bands on/off. The live chart has no toolbar of its own,
     *  so its toggle lives here; in candles mode TradingView's toolbar
     *  carries the same control and this one is not rendered. */
    liqShown?: boolean;
    onToggleLiq?: (next: boolean) => void;
    /** Support/resistance on/off, same placement rule as the liq toggle. */
    srShown?: boolean;
    onToggleSr?: (next: boolean) => void;
}

const Divider: React.FC<{ className?: string }> = ({ className }) => (
    <span
        aria-hidden="true"
        className={cn('h-6 w-px shrink-0 bg-border', className)}
    />
);

/**
 * Strip above the chart: base-token market picker · live price with
 * realtime tick arrow · 24h change · funding · max leverage (each separated
 * by dividers), and the icon-only Live/Candles chart-type switch on the
 * right. Mobile keeps the essentials: picker, arrow + price, chart-type
 * icons. Occupies the `stats` grid area at every width.
 *
 * The picker swaps by width: below the markets sidebar's 1024px gate it's
 * the searchable MobileMarketSheet (a dropdown over ~230 markets is
 * unusable on a phone, and there's no sidebar to fall back on); at 1024px+
 * the compact TokenPairSelect, with the sidebar carrying search/favorites.
 * Only one is mounted — the closed Radix Select keeps an item subtree per
 * market alive, so rendering both would double that for nothing.
 */
/** Remembered across sessions: someone who wants the numbers up wants them up. */
const MARKET_STATS_STORAGE_KEY = 'snappy.marketStats.v1';

function readStatsOpen(): boolean {
    try {
        return window.localStorage.getItem(MARKET_STATS_STORAGE_KEY) === '1';
    } catch {
        // private mode, or storage blocked — closed is the safe default
        return false;
    }
}

/**
 * One label/value pair on the phone-only stats line. Laid out inline rather
 * than stacked like the desktop columns: stacking would double the height of
 * a row whose whole purpose is to cost the chart as little as possible.
 */
const MobileStat: React.FC<{
    label: string;
    value: string;
    className?: string;
}> = ({ label, value, className }) => (
    <span className="flex shrink-0 items-baseline gap-1">
        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {label}
        </span>
        <span
            className={cn(
                'font-mono text-[11px] font-bold tabular-nums text-foreground/80',
                className,
            )}
        >
            {value}
        </span>
    </span>
);

const SpeedChartHeaderImpl: React.FC<SpeedChartHeaderProps> = ({
    currentValue,
    priceFlash,
    priceConfig,
    chartMode,
    onChartModeChange,
    liqShown = true,
    onToggleLiq,
    srShown = false,
    onToggleSr,
}) => {
    const networkId = useTokenPairStore((s) => s.networkId);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const { data } = useTokenPairs(networkId);
    const hasSidebar = useMediaQuery('(min-width: 1024px)');

    const market = findMarket(data?.markets, selectedCoin);
    const change = market
        ? formatDayChange(market.markPx, market.prevDayPx ?? null)
        : null;
    const funding = market ? formatFunding(market.funding) : null;
    // Both of these were already in every markets response and rendered
    // nowhere. Open interest is the "how crowded is this trade" figure; the
    // mark/oracle gap is what says the market is thin enough to be a trap.
    const openInterest = market
        ? formatOpenInterest(market.openInterest, market.markPx)
        : null;
    const dislocated = market
        ? isDislocated(market.markPx, market.oraclePx)
        : false;
    const dislocation = market
        ? formatDislocation(market.markPx, market.oraclePx)
        : null;

    // Direction of the LAST realtime tick (persists between ticks, unlike
    // the transient priceFlash) — drives the arrow next to the price.
    // Derived during render (change-guarded ref writes) instead of
    // setState-in-effect: the header already re-renders on every
    // currentValue tick, and the effect version rendered it a second time
    // per tick just to flip the arrow.
    // Collapsed by default so the chart keeps its height: on a phone the
    // `stats` row steals directly from the only flexible grid row. Persisted,
    // because someone who wants the numbers up wants them up every session.
    const [statsOpen, setStatsOpen] = useState(readStatsOpen);
    const toggleStats = () => {
        setStatsOpen((open) => {
            const next = !open;
            try {
                window.localStorage.setItem(
                    MARKET_STATS_STORAGE_KEY,
                    next ? '1' : '0',
                );
            } catch {
                // storage unavailable — the choice just won't persist
            }
            return next;
        });
    };

    const prevValueRef = useRef(currentValue);
    const tickDirRef = useRef<'up' | 'down' | null>(null);
    if (currentValue !== prevValueRef.current) {
        tickDirRef.current =
            currentValue > prevValueRef.current ? 'up' : 'down';
        prevValueRef.current = currentValue;
    }
    const tickDir = tickDirRef.current;

    return (
        <Panel
            as="section"
            aria-label="Market price"
            className="flex w-full flex-col"
            style={{ gridArea: 'stats' }}
        >
            <div className="flex w-full flex-row items-center gap-2.5 px-3 py-1.5 sm:gap-3.5 sm:px-4">
                {/* Market picker — searchable sheet below 1024px, compact
                select once the markets sidebar takes over */}
                {hasSidebar ? (
                // Never disabled by an open position: one position per coin is
                // allowed, so this select is how the user reaches the next
                // market to trade. It was the fourth and last of the market
                // pickers still enforcing the single-position lock.
                    <TokenPairSelect
                        iconOnly
                        className="h-8 w-auto shrink-0 border-0 bg-transparent px-1 shadow-none"
                    />
                ) : (
                    <MobileMarketSheet />
                )}

                <Divider />

                {/* Realtime tick arrow + live price */}
                <span className="flex min-w-0 items-center gap-1.5">
                    {tickDir && (
                        <span
                            aria-hidden="true"
                            className={cn(
                                'text-sm leading-none',
                                tickDir === 'up'
                                    ? 'text-primary'
                                    : 'text-destructive',
                            )}
                        >
                            {tickDir === 'up' ? '▲' : '▼'}
                        </span>
                    )}
                    <span
                        className={cn(
                            'min-w-0 truncate font-mono text-xl font-semibold tabular-nums tracking-tight text-foreground sm:text-2xl',
                            priceFlash === 'up' && 'price-flash-up',
                            priceFlash === 'down' && 'price-flash-down',
                        )}
                    >
                    ${priceConfig.formatDisplay(currentValue)}
                    </span>
                </span>

                {/* Dislocation warning. NOT breakpoint-gated like the stats
                below it: this is the only thing in the strip that should stop
                someone, and a phone is exactly where a market gets picked by
                mistake. Hyperliquid liquidates against the mark and funds
                against the oracle, so at 40x a 1% gap is wider than the whole
                maintenance margin — and on this board every gap that size
                belongs to a market with no open interest at all. */}
                {dislocated && dislocation && (
                    <>
                        <Divider />
                        <span
                            title={`The mark price is ${dislocation} away from the oracle. Markets this far apart are usually dead — check the open interest before taking leverage here.`}
                            className="flex shrink-0 items-center gap-1 rounded-md border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-amber-300"
                        >
                            <AlertTriangle size={11} className="shrink-0" />
                            <span className="font-mono text-[10px] font-bold tabular-nums">
                                {dislocation}
                            </span>
                            <span className="hidden text-[9px] font-semibold uppercase tracking-[0.08em] sm:inline">
                            off oracle
                            </span>
                        </span>
                    </>
                )}

                {/* 24h change (desktop only) */}
                {change && (
                    <>
                        <Divider className="hidden sm:block" />
                        <span className="hidden shrink-0 flex-col leading-tight sm:flex">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Change 24h
                            </span>
                            <span
                                className={cn(
                                    'font-mono text-xs font-bold tabular-nums',
                                    change.dir > 0 && 'text-primary',
                                    change.dir < 0 && 'text-destructive',
                                    change.dir === 0 && 'text-muted-foreground',
                                )}
                            >
                                {change.text}
                            </span>
                        </span>
                    </>
                )}

                {/* Funding 1h — gated a breakpoint later than its neighbours:
                it's the least urgent of the three meta columns and four of
                them crowd the strip at 640px. */}
                {funding && (
                    <>
                        <Divider className="hidden md:block" />
                        <span className="hidden shrink-0 flex-col leading-tight md:flex">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Funding 1h
                            </span>
                            <span
                                className={cn(
                                    'font-mono text-xs font-bold tabular-nums',
                                    funding.startsWith('-')
                                        ? 'text-destructive/90'
                                        : 'text-foreground/80',
                                )}
                            >
                                {funding}
                            </span>
                        </span>
                    </>
                )}

                {/* Open interest — the position everyone else is already holding
                here. Shares the `md` breakpoint with funding: both are context
                rather than the price, and four columns crowd the strip at
                640px. */}
                {openInterest && (
                    <>
                        <Divider className="hidden md:block" />
                        <span className="hidden shrink-0 flex-col leading-tight md:flex">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Open int
                            </span>
                            <span className="font-mono text-xs font-bold tabular-nums text-foreground/80">
                                {openInterest}
                            </span>
                        </span>
                    </>
                )}

                {/* Max leverage (desktop only) */}
                {market && (
                    <>
                        <Divider className="hidden sm:block" />
                        <span className="hidden shrink-0 flex-col leading-tight sm:flex">
                            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            Max lev
                            </span>
                            <span className="font-mono text-xs font-bold tabular-nums text-foreground/80">
                                {market.maxLeverage}×
                            </span>
                        </span>
                    </>
                )}

                {/* Chart type — icon-only Live/Candles switch */}
                <span className="ml-auto flex shrink-0 items-center gap-2">
                    {/* Liquidation zones, live mode only: candles mode has the
                        same button inside TradingView's toolbar, next to
                        Indicators, and two of the same control would be noise. */}
                    {chartMode === 'live' && onToggleLiq && (
                        <button
                            type="button"
                            onClick={() => onToggleLiq(!liqShown)}
                            aria-pressed={liqShown}
                            title={
                                liqShown
                                    ? 'Hide liquidation zones'
                                    : 'Show liquidation zones'
                            }
                            className={cn(
                                'flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                                liqShown
                                    ? 'bg-primary/12 text-primary'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <Layers size={13} />
                            <span className="hidden sm:inline">Liq zones</span>
                        </button>
                    )}
                    {chartMode === 'live' && onToggleSr && (
                        <button
                            type="button"
                            onClick={() => onToggleSr(!srShown)}
                            aria-pressed={srShown}
                            title={
                                srShown
                                    ? 'Hide support and resistance'
                                    : 'Show support and resistance'
                            }
                            className={cn(
                                'flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                                srShown
                                    ? 'bg-primary/12 text-primary'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <Ruler size={13} />
                            <span className="hidden sm:inline">S/R</span>
                        </button>
                    )}
                    {/* Phone-only: the stats to the left of this are all gated at
                    `sm`/`md`, so below that this button is the only way to
                    them. */}
                    <button
                        type="button"
                        onClick={toggleStats}
                        aria-expanded={statsOpen}
                        aria-label="Market details"
                        title="Market details"
                        className={cn(
                            'flex cursor-pointer items-center rounded-md p-1.5 transition-colors md:hidden',
                            statsOpen
                                ? 'bg-primary/12 text-primary'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        <Info size={13} />
                    </button>
                    <span className="hidden text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:block">
                    Chart type
                    </span>
                    <span className="flex gap-0.5 rounded-lg border border-border bg-secondary/50 p-0.5">
                        <button
                            type="button"
                            onClick={() => onChartModeChange('live')}
                            aria-pressed={chartMode === 'live'}
                            aria-label="Live chart"
                            title="Live chart"
                            className={cn(
                                'flex cursor-pointer items-center rounded-md px-2 py-1.5 transition-colors',
                                chartMode === 'live'
                                    ? 'bg-primary/12 text-primary'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <ChartLine size={13} />
                        </button>
                        <button
                            type="button"
                            onClick={() => onChartModeChange('candles')}
                            aria-pressed={chartMode === 'candles'}
                            aria-label="Candles chart"
                            title="TradingView chart with full history"
                            className={cn(
                                'flex cursor-pointer items-center rounded-md px-2 py-1.5 transition-colors',
                                chartMode === 'candles'
                                    ? 'bg-primary/12 text-primary'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            <ChartCandlestick size={13} />
                        </button>
                    </span>
                </span>
            </div>

            {/* The same figures the desktop strip shows inline. Below `md`
                they have nowhere to go — a phone was seeing the pair and the
                price and nothing else — so they hide behind the ⓘ above and
                open as one short line. */}
            {statsOpen && (
                <div
                    role="group"
                    aria-label="Market details"
                    className="flex items-center gap-3 overflow-x-auto border-t border-border/40 px-3 py-1 md:hidden"
                >
                    {change && (
                        <MobileStat
                            label="24h"
                            value={change.text}
                            className={cn(
                                change.dir > 0 && 'text-primary',
                                change.dir < 0 && 'text-destructive',
                            )}
                        />
                    )}
                    {openInterest && (
                        <MobileStat label="OI" value={openInterest} />
                    )}
                    {funding && <MobileStat label="Fund" value={funding} />}
                    {market && (
                        <MobileStat
                            label="Max"
                            value={`${market.maxLeverage}×`}
                        />
                    )}
                </div>
            )}
        </Panel>
    );
};

// Memoized: the parent re-renders per price tick.
const SpeedChartHeader = React.memo(SpeedChartHeaderImpl);

export default SpeedChartHeader;
